'use client';

import DOMPurify from 'isomorphic-dompurify';
import type { BlogPost } from '@/lib/api/blog';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Calendar, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Typography } from '@/components/ui/typography';
import { BlogSocialShare } from '@/components/frontend/blog/blog-social-share';
import Image from 'next/image';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
interface BlogPostDetailProps {
    post: BlogPost;
    locale: string;
}

export function BlogPostDetail({ post, locale }: BlogPostDetailProps) {
    const introSectionRef = useScrollAnimation({ threshold: 0.1 });
    // Parse and sanitize content from JSON array if needed (server-side)
    let html = post.content;
    if (typeof post.content === 'string') {
        try {
            const parsed = JSON.parse(post.content);
            if (Array.isArray(parsed)) {
                html = parsed.join('');
            } else {
                html = parsed;
            }
        } catch {
            // If parsing fails, use content as-is
            html = post.content;
        }
    }

    // Convert className to class for HTML compatibility
    html = html.replace(/\bclassName=/g, 'class=');

    // Sanitize HTML to prevent XSS attacks (server-side)
    let sanitizedHtml = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'p', 'br', 'hr',
            'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small', 'sub', 'sup',
            'ul', 'ol', 'li',
            'blockquote', 'pre', 'code',
            'a', 'img', 'figure', 'figcaption',
            'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
            'div', 'span', 'section', 'article',
        ],
        ALLOWED_ATTR: [
            'href', 'target', 'rel', 'title',
            'src', 'alt', 'width', 'height', 'loading', 'referrerpolicy', 'crossorigin',
            'class', 'id',
            'colspan', 'rowspan',
        ],
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
        // Add hooks to enforce additional security on images
        ADD_ATTR: ['loading', 'referrerpolicy'], // Ensure these attributes are preserved
        FORBID_ATTR: ['onerror', 'onload', 'oninput'], // Block event handlers
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style'], // Block potentially dangerous tags
        ALLOW_DATA_ATTR: false, // Don't allow data-* attributes
        RETURN_TRUSTED_TYPE: false,
    });

    // Post-process to add security attributes to images
    // Add loading="lazy" and referrerpolicy="no-referrer" to all images that don't have them
    sanitizedHtml = sanitizedHtml.replace(
        /<img([^>]*?)>/gi,
        (match, attrs) => {
            // Check if loading attribute exists
            const hasLoading = /loading=/i.test(attrs);
            // Check if referrerpolicy exists
            const hasReferrerPolicy = /referrerpolicy=/i.test(attrs);

            let newAttrs = attrs;
            if (!hasLoading) {
                newAttrs += ' loading="lazy"';
            }
            if (!hasReferrerPolicy) {
                newAttrs += ' referrerpolicy="no-referrer"';
            }

            return `<img${newAttrs}>`;
        }
    );

    const contentHtml = sanitizedHtml;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // Generate full URL for sharing (blog posts live at /[locale]/[slug], no blog prefix)
    const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://businessmlmsoftware.com';
    const fullUrl = `${baseUrl}/${locale}/${post.slug}`;

    // Parse custom schemaMarkup (extracts JSON from <script> tags or treats as raw JSON)
    const renderSchemas = () => {
        if (post.schemaMarkup?.trim()) {
            const scriptRegex = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
            const jsonBlocks: string[] = [];
            let match;
            while ((match = scriptRegex.exec(post.schemaMarkup)) !== null) {
                jsonBlocks.push(match[1].trim());
            }
            if (jsonBlocks.length === 0) {
                // No script tags — treat whole value as raw JSON
                jsonBlocks.push(post.schemaMarkup.trim());
            }
            return jsonBlocks.map((json, i) => (
                <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
            ));
        }
        // Fallback: auto-generated article schema
        const articleSchema = {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.description,
            image: post.image ? [post.image] : undefined,
            datePublished: post.date,
            dateModified: post.date,
            inLanguage: locale,
            author: {
                '@type': 'Person',
                name: post.author || 'Business MLM Software',
            },
            publisher: {
                '@type': 'Organization',
                name: 'Business MLM Software',
                url: baseUrl,
            },
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': fullUrl,
            },
            url: fullUrl,
        };
        return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />;
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black">
            {/* JSON-LD Schema for SEO */}
            {renderSchemas()}
            {/* Enhanced Hero Banner with Blog Info */}
            <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-900">
                {/* Background without image */}
                <>
                    <div
                        className="absolute inset-0 pointer-events-none bg-cover bg-center"
                    />
                    <div className="absolute inset-0 bg-white/20 dark:bg-[#0b1223]/40 pointer-events-none" />
                </>
                <div className="absolute -right-24 -top-16 h-48 w-48 rounded-full bg-indigo-900/30 dark:bg-indigo-600/20 blur-3xl pointer-events-none" />
                <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-slate-800/40 dark:bg-slate-600/30 blur-3xl pointer-events-none" />

                <div className="relative z-10 px-4 py-12 md:py-16">
                    <div className="container mx-auto px-4">
                        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                            {/* Left Column - Content */}
                            <div className="text-left order-2 lg:order-1">
                                {/* Title */}
                                <Typography
                                    variant="h1"
                                    className={cn(
                                        'mb-6 !text-xl font-bold leading-tight md:!text-2xl lg:!text-3xl',
                                        'text-gray-900 dark:text-white'
                                    )}
                                >
                                    {post.title}
                                </Typography>

                                {/* Description */}
                                {post.description && (
                                    <p
                                        className={cn(
                                            'mb-8 text-lg md:text-lg max-w-3xl leading-relaxed',
                                            'text-gray-600 dark:text-gray-400'
                                        )}
                                    >
                                        {post.description}
                                    </p>
                                )}

                                {/* Meta Info: Date & Author */}
                                <div
                                    className={cn(
                                        'flex flex-wrap items-center justify-start gap-4 text-sm mb-8',
                                        'text-gray-600 dark:text-gray-400'
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4" />
                                        <time>{formatDate(post.date)}</time>
                                    </div>
                                    {post.author && (
                                        <>
                                            <span>•</span>
                                            <div className="flex items-center gap-2">
                                                <User className="h-4 w-4" />
                                                <span>By {post.author}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Social Share Buttons */}
                                <BlogSocialShare
                                    shareUrl={fullUrl}
                                    shareTitle={post.title}
                                    hasImage={!!post.image}
                                    align="left"
                                />
                            </div>

                            {/* Right Column - Feature Image */}
                            {post.image && (
                                <div className="relative order-1 lg:order-2">
                                    <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-video">
                                        {post.image.startsWith('/uploads/') || post.image.startsWith('/images/') ? (
                                            // Use regular img tag for local files
                                            <img
                                                src={post.image}
                                                alt={post.title}
                                                className="w-full h-full object-cover rounded-2xl"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                        ) : (
                                            // Use Next.js Image for remote URLs (Cloudinary)
                                            <Image
                                                src={post.image}
                                                alt={post.title}
                                                fill
                                                className="object-cover rounded-2xl"
                                                sizes="(max-width: 768px) 100vw, 50vw"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>



            <SectionWrapper variant="default" padding="md" className="relative overflow-hidden blog-post-detail">
                <article className="container mx-auto px-4">
                    <div
                        className="blog-content-wrapper"
                        dangerouslySetInnerHTML={{ __html: contentHtml }}
                    />
                </article>
            </SectionWrapper>
        </div>
    );
}

